import React from 'react'
import { Link } from 'react-router-dom'
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  FileText, 
  Plus,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { useApp } from '../context/AppContext'
import { format } from 'date-fns'

const stats = [
  {
    name: 'Outstanding',
    value: '$6,299',
    change: '+12.5%',
    trend: 'up',
    icon: DollarSign,
    color: 'primary'
  },
  {
    name: 'Paid This Month',
    value: '$4,799',
    change: '+8.2%',
    trend: 'up',
    icon: TrendingUp,
    color: 'accent'
  },
  {
    name: 'Overdue',
    value: '$1,099',
    change: '-3.1%',
    trend: 'down',
    icon: AlertCircle,
    color: 'danger'
  },
  {
    name: 'Draft Invoices',
    value: '1',
    change: '0%',
    trend: 'neutral',
    icon: FileText,
    color: 'gray'
  }
]

const recentInvoices = [
  { id: 1, number: 'INV-2024-0005', client: 'TechStart Inc', amount: '$6,000', status: 'partial', date: 'Mar 1, 2024' },
  { id: 2, number: 'INV-2024-0004', client: 'Global Solutions Ltd', amount: '$6,500', status: 'draft', date: 'Mar 15, 2024' },
  { id: 3, number: 'INV-2024-0003', client: 'Acme Corporation', amount: '$1,099', status: 'overdue', date: 'Jan 1, 2024' },
  { id: 4, number: 'INV-2024-0002', client: 'TechStart Inc', amount: '$6,200', status: 'sent', date: 'Feb 1, 2024' },
  { id: 5, number: 'INV-2024-0001', client: 'Acme Corporation', amount: '$4,799', status: 'paid', date: 'Jan 20, 2024' },
]

const quickActions = [
  { name: 'New Invoice', href: '/invoices/new', icon: Plus, color: 'primary' },
  { name: 'New Client', href: '/clients', icon: Plus, color: 'accent' },
  { name: 'View Reports', href: '/reports', icon: TrendingUp, color: 'primary' },
  { name: 'Settings', href: '/settings', icon: Clock, color: 'gray' },
]

const getStatusBadge = (status) => {
  const styles = {
    paid: 'bg-accent-100 text-accent-700',
    partial: 'bg-yellow-100 text-yellow-700',
    sent: 'bg-blue-100 text-blue-700',
    overdue: 'bg-red-100 text-red-700',
    draft: 'bg-gray-100 text-gray-700',
  }
  return styles[status] || styles.draft
}

export default function Dashboard() {
  const { dashboardMetrics, invoices, clients, getClientById } = useApp()

  // Calculate metrics from actual data
  const outstanding = invoices
    .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
    .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0)

  const paidThisMonth = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.total, 0)

  const overdue = invoices
    .filter(inv => inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.total, 0)

  const recentInvoicesList = [...invoices]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Welcome back!</h2>
          <p className="text-gray-600 mt-1">Here's what's happening with your invoices today.</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary">
          <Plus size={20} />
          <span>New Invoice</span>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            name: 'Outstanding',
            value: `$${outstanding.toLocaleString()}`,
            trend: '+12.5%',
            trendUp: true,
            icon: DollarSign,
            color: 'primary'
          },
          {
            name: 'Paid This Month',
            value: `$${paidThisMonth.toLocaleString()}`,
            trend: '+8.2%',
            trendUp: true,
            icon: TrendingUp,
            color: 'accent'
          },
          {
            name: 'Overdue',
            value: `$${overdue.toLocaleString()}`,
            trend: '-3.1%',
            trendUp: true,
            icon: AlertCircle,
            color: 'danger'
          },
          {
            name: 'Total Clients',
            value: clients.length.toString(),
            trend: '+2',
            trendUp: true,
            icon: Clock,
            color: 'gray'
          }
        ].map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="card p-5">
              <div className="flex items-center justify-between">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  stat.color === 'primary' ? 'bg-primary-100' :
                  stat.color === 'accent' ? 'bg-accent-100' :
                  stat.color === 'danger' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  <Icon size={24} className={
                    stat.color === 'primary' ? 'text-primary-600' :
                    stat.color === 'accent' ? 'text-accent-600' :
                    stat.color === 'danger' ? 'text-red-600' :
                    'text-gray-600'
                  } />
                </div>
                <span className={`flex items-center text-sm font-medium ${
                  stat.trendUp ? 'text-accent-600' : 'text-red-600'
                }`}>
                  {stat.trendUp ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                  {stat.trend}
                </span>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.name}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <Link
              key={action.name}
              to={action.href}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
                action.color === 'primary' ? 'bg-primary-50 border-primary-100 hover:border-primary-200' :
                action.color === 'accent' ? 'bg-accent-50 border-accent-100 hover:border-accent-200' :
                'bg-gray-50 border-gray-100 hover:border-gray-200'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                action.color === 'primary' ? 'bg-primary-100' :
                action.color === 'accent' ? 'bg-accent-100' :
                'bg-gray-200'
              }`}>
                <Icon size={20} className={
                  action.color === 'primary' ? 'text-primary-600' :
                  action.color === 'accent' ? 'text-accent-600' :
                  'text-gray-600'
                } />
              </div>
              <span className="font-medium text-gray-900">{action.name}</span>
            </Link>
          )
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Recent Invoices</h3>
            <Link to="/invoices" className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentInvoicesList.map((invoice) => {
              const client = getClientById(invoice.clientId)
              return (
                <Link
                  key={invoice.id}
                  to={`/invoices/${invoice.id}/view`}
                  className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      invoice.status === 'paid' ? 'bg-accent-100' :
                      invoice.status === 'partial' ? 'bg-yellow-100' :
                      invoice.status === 'overdue' ? 'bg-red-100' :
                      invoice.status === 'sent' ? 'bg-blue-100' :
                      'bg-gray-100'
                    }`}>
                      {invoice.status === 'paid' ? (
                        <CheckCircle2 size={20} className="text-accent-600" />
                      ) : (
                        <FileText size={20} className={
                          invoice.status === 'partial' ? 'text-yellow-600' :
                          invoice.status === 'overdue' ? 'text-red-600' :
                          invoice.status === 'sent' ? 'text-blue-600' :
                          'text-gray-600'
                        } />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                      <p className="text-sm text-gray-500">{client?.name || 'Unknown Client'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">${invoice.total.toLocaleString()}</p>
                    <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Pending Actions */}
        <div className="card">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Pending Actions</h3>
          </div>
          <div className="p-4 space-y-3">
            {invoices
              .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
              .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
              .slice(0, 4)
              .map((invoice) => {
                const client = getClientById(invoice.clientId)
                const daysUntilDue = Math.ceil((new Date(invoice.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
                const isOverdue = daysUntilDue < 0

                return (
                  <div key={invoice.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                    <div className={`w-2 h-2 rounded-full ${
                      isOverdue ? 'bg-red-500' : daysUntilDue <= 3 ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{client?.name}</p>
                      <p className="text-xs text-gray-500">
                        {isOverdue 
                          ? `${Math.abs(daysUntilDue)} days overdue` 
                          : `${daysUntilDue} days until due`
                        }
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">${invoice.total.toLocaleString()}</span>
                  </div>
                )
              })}
          </div>
        </div>
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Revenue Overview</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <TrendingUp size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Revenue chart visualization</p>
            <p className="text-sm text-gray-400 mt-1">Connect to your analytics for detailed insights</p>
          </div>
        </div>
      </div>
    </div>
  )
}
